import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  type MemberSessionTranscriptCopyState,
  type MemberSessionTranscriptStatus,
  MemberSessionTranscriptView,
} from './MemberSessionTranscriptView';
import {
  concatenateTranscriptMarkdown,
  formatTranscriptDateLabel,
} from './transcriptUtils';

import { useBackNavigation } from '@/hooks/useBackNavigation';
import {
  buildMemberMomentsAnalyticsProperties,
  MEMBER_MOMENTS_EVENTS,
  momentsAnalytics,
} from '@/services/momentsAnalytics';
import { useGetMomentsSessionTranscriptsQuery } from '@/store/api/momentsApi';

const getErrorStatus = (error: unknown): string | number | undefined => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return undefined;
  }
  return (error as { status?: string | number }).status;
};

const isNotFoundOrAccessDenied = (error: unknown): boolean => {
  const status = getErrorStatus(error);
  return status === 403 || status === 404;
};

const getTranscriptStatus = ({
  error,
  hasData,
  isError,
  isLoading,
  sessionId,
}: {
  error: unknown;
  hasData: boolean;
  isError: boolean;
  isLoading: boolean;
  sessionId: string;
}): MemberSessionTranscriptStatus => {
  if (!sessionId.trim()) {
    return 'not-found';
  }
  if (hasData) {
    return 'ready';
  }
  if (isLoading) {
    return 'loading';
  }
  if (isError) {
    return isNotFoundOrAccessDenied(error) ? 'not-found' : 'error';
  }
  return 'not-found';
};

const MemberSessionTranscriptPage = () => {
  const { dateSlug, sessionId = '' } = useParams<{
    dateSlug: string;
    sessionId: string;
  }>();
  const [copyState, setCopyState] =
    useState<MemberSessionTranscriptCopyState>('idle');
  const trimmedSessionId = sessionId.trim();
  const canLoadTranscript = Boolean(trimmedSessionId);
  const transcriptArg = canLoadTranscript
    ? { includeContent: true, sessionId: trimmedSessionId }
    : skipToken;
  const {
    currentData: transcriptsData,
    error: transcriptsError,
    isError: isTranscriptsError,
    isFetching: isTranscriptsFetching,
    isLoading: isTranscriptsLoading,
  } = useGetMomentsSessionTranscriptsQuery(transcriptArg);

  const status = getTranscriptStatus({
    error: transcriptsError,
    hasData: Boolean(transcriptsData),
    isError: isTranscriptsError,
    isLoading: isTranscriptsLoading || isTranscriptsFetching,
    sessionId: trimmedSessionId,
  });

  const transcriptBody = useMemo(
    () => concatenateTranscriptMarkdown(transcriptsData?.items ?? []),
    [transcriptsData?.items],
  );
  const dateLabel = useMemo(
    () =>
      formatTranscriptDateLabel(transcriptsData?.session.plannedStartAt) ??
      formatTranscriptDateLabel(dateSlug),
    [dateSlug, transcriptsData?.session.plannedStartAt],
  );

  useEffect(() => {
    if (status !== 'ready' || !transcriptBody.trim()) {
      return;
    }
    momentsAnalytics.viewed(
      MEMBER_MOMENTS_EVENTS.viewedTranscript,
      'member_transcript',
      window.location.pathname,
      buildMemberMomentsAnalyticsProperties({
        route: window.location.pathname,
        sessionId: trimmedSessionId,
        surface: 'transcript_detail',
      }),
      {
        dedupeKey: `transcript:${trimmedSessionId}:${window.location.pathname}`,
      },
    );
  }, [status, transcriptBody, trimmedSessionId]);

  useEffect(() => {
    if (copyState === 'idle') {
      return undefined;
    }
    const timeout = window.setTimeout(() => setCopyState('idle'), 2000);
    return () => window.clearTimeout(timeout);
  }, [copyState]);

  const handleBack = useBackNavigation();

  const handleCopy = useCallback(async () => {
    if (!transcriptBody.trim()) {
      setCopyState('error');
      return;
    }
    try {
      await navigator.clipboard.writeText(transcriptBody);
      momentsAnalytics.track(
        MEMBER_MOMENTS_EVENTS.copiedTranscript,
        buildMemberMomentsAnalyticsProperties({
          copyKind: 'transcript',
          route: window.location.pathname,
          sessionId: trimmedSessionId,
          surface: 'transcript_detail',
        }),
      );
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
  }, [transcriptBody, trimmedSessionId]);

  return (
    <MemberSessionTranscriptView
      copyState={copyState}
      dateLabel={dateLabel}
      errorMessage={
        status === 'error'
          ? 'Failed to load transcript. Please try again later.'
          : undefined
      }
      status={status}
      transcriptBody={transcriptBody}
      onBack={handleBack}
      onCopy={handleCopy}
    />
  );
};

export default MemberSessionTranscriptPage;
